import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { evaluatePermission, PermissionRule } from '../../utils/permissionEngine';

interface MenuPermissionGateProps {
  children: React.ReactNode;
  /**
   * Required roles (OR logic - user needs at least one)
   */
  requiredRoles?: string[];
  /**
   * Required capabilities (AND logic - user needs all)
   */
  requiredCapabilities?: string[];
  /**
   * Required capabilities with OR logic (user needs at least one)
   */
  anyCapability?: string[];
  /**
   * Hide from customer/subscriber portal users
   */
  staffOnly?: boolean;
  /**
   * Always show to customer/subscriber portal users
   */
  allowCustomer?: boolean;
}

/**
 * MenuPermissionGate component for sidebar menu items
 * 
 * Similar to PermissionGate but specifically for menu items.
 * Returns null if user lacks permission (hides menu item).
 */
const MenuPermissionGate: React.FC<MenuPermissionGateProps> = ({
  children,
  requiredRoles,
  requiredCapabilities,
  anyCapability,
  staffOnly,
  allowCustomer,
}) => {
  const { permissions, loading, isCustomerPortalUser, isSuperAdmin, hasRole } = useAuth();

  if (loading && (staffOnly || allowCustomer)) {
    return null;
  }

  const isCustomer = isCustomerPortalUser();

  if (isCustomer) {
    if (staffOnly) return null;
    if (allowCustomer) return <>{children}</>;
    return null;
  }

  // Staff: allowCustomer means "customers can also see this", not "hide from admin".
  if (!requiredRoles && !requiredCapabilities && !anyCapability) {
    return <>{children}</>;
  }

  if (isSuperAdmin() || hasRole('administrator')) {
    return <>{children}</>;
  }

  const rule: PermissionRule = {
    roles: requiredRoles,
    capabilities: requiredCapabilities,
    anyCapability: anyCapability,
  };

  if (!evaluatePermission(rule, permissions)) {
    return null;
  }

  return <>{children}</>;
};

export default MenuPermissionGate;
